/** @jsx jsx */
import { jsx } from '@emotion/core'
import { RadioFieldContainer, RadioFieldContainerProps } from 'components/form/field/RadioFieldContainer'
import { useCallback, useRef } from 'react'
import { useForm } from 'react-final-form'

import { resolveName } from '../final-form'

interface ConfirmChangeRadioFieldContainerProps<T> extends Omit<RadioFieldContainerProps, 'label'> {
  label?: React.ReactNode
  onAskUserConfirmation: ((currentValue: T, previousValue: T) => boolean | Promise<boolean>) | null
}

export function ConfirmChangeRadioFieldContainer<T>(props: Readonly<ConfirmChangeRadioFieldContainerProps<T>>) {
  const { onAskUserConfirmation, ...radioProps } = props
  const form = useForm()

  const isConfirmandoTrocaRef = useRef(false)
  const radioRef = useRef<HTMLDivElement>(null)

  const handleTentativaTroca = useCallback(
    async (e: React.SyntheticEvent) => {
      if (!onAskUserConfirmation || radioProps.disabled) return

      const fieldName = resolveName(radioProps.name)
      const previousValue = form.getFieldState(fieldName)?.value as T
      const currentValue = radioProps.value as T

      if (Object.is(previousValue, currentValue)) return
      if (isConfirmandoTrocaRef.current) return

      e.preventDefault()
      e.stopPropagation()

      isConfirmandoTrocaRef.current = true

      try {
        const trocaConfirmada = (await onAskUserConfirmation(currentValue, previousValue)) !== false
        if (!trocaConfirmada) return

        form.change(fieldName, currentValue)
        radioRef.current?.focus()
      } finally {
        isConfirmandoTrocaRef.current = false
      }
    },
    [form, onAskUserConfirmation, radioProps.disabled, radioProps.name, radioProps.value]
  )

  const handleMouseDownCapture = useCallback(
    (e: React.MouseEvent) => {
      handleTentativaTroca(e)
    },
    [handleTentativaTroca]
  )

  const handleKeyDownCapture = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        handleTentativaTroca(e)
      }
    },
    [handleTentativaTroca]
  )

  return (
    <div
      {...(onAskUserConfirmation
        ? {
            onMouseDownCapture: handleMouseDownCapture,
            onKeyDownCapture: handleKeyDownCapture,
          }
        : null)}
    >
      <RadioFieldContainer {...radioProps} innerRef={radioRef} />
    </div>
  )
}
